/**
 * Ingredient Text Normalization
 *
 * Normalizes conversational/natural language input into clean, structured ingredient format:
 * - Converts written-out numbers to digits ("two dozen eggs" → "2 dozen eggs")
 * - Removes filler words ("I need some chicken" → "chicken")
 * - Removes articles ("a chicken" → "chicken")
 * - Abbreviates units ("pounds" → "lbs")
 * - Splits multiple items ("eggs and milk" → "eggs\nmilk")
 * - Detects multiple items ("two apples three bananas" → "2 apples\n3 bananas")
 *
 * Applied to ALL input sources: typed text, voice recognition, paste, etc.
 * Voice-specific: normalization is applied immediately to give instant visual feedback
 */

import type { Locale } from "@/i18n";

/**
 * Auto-detect locale from text content
 * Chinese characters → zh, otherwise → en
 */
function detectLocale(text: string): Locale {
  // Check for Chinese characters (CJK Unified Ideographs)
  const hasChinese = /[\u4e00-\u9fff]/.test(text);
  return hasChinese ? "zh-CN" : "en";
}

// ============================================================================
// Number Conversion (Written-out to Digits)
// ============================================================================

/**
 * Convert written-out numbers to digits
 * Handles: basic numbers, compound numbers, fractions, decimals
 *
 * @example
 * convertSpokenNumbers("two apples") // "2 apples"
 * convertSpokenNumbers("twenty three eggs") // "23 eggs"
 * convertSpokenNumbers("one and a half cups") // "1.5 cups"
 * convertSpokenNumbers("a quarter pound") // "1/4 pound"
 */
function convertSpokenNumbers(text: string, locale: Locale): string {
  if (locale !== "en") {
    // Chinese text preserved as-is
    return text;
  }

  let result = text;

  // IMPORTANT: Convert "X and a half" FIRST before any other "and" processing
  // Must happen before fractions to avoid conflicts

  // Handle word numbers + "and a/one half" → decimal
  result = result.replace(/\bone\s+and\s+(?:a|one)\s+half\b/gi, "1.5");
  result = result.replace(/\btwo\s+and\s+(?:a|one)\s+half\b/gi, "2.5");
  result = result.replace(/\bthree\s+and\s+(?:a|one)\s+half\b/gi, "3.5");
  result = result.replace(/\bfour\s+and\s+(?:a|one)\s+half\b/gi, "4.5");
  result = result.replace(/\bfive\s+and\s+(?:a|one)\s+half\b/gi, "5.5");

  // Handle digit + "and a/one half" → decimal
  result = result.replace(/\b(\d+)\s+and\s+(?:a|one)\s+half\b/gi, "$1.5");

  // 1. Convert common fractions (after "and a half" to avoid conflicts)
  const fractionMap: Record<string, string> = {
    "a quarter": "1/4",
    "one quarter": "1/4",
    "a half": "1/2",
    "one half": "1/2",
    "half a": "1/2",
    "half an": "1/2",
    "three quarters": "3/4",
    "two thirds": "2/3",
    "one third": "1/3",
    "an eighth": "1/8",
    "a eighth": "1/8",
  };

  for (const [spoken, numeric] of Object.entries(fractionMap)) {
    const regex = new RegExp(`\\b${spoken}\\b`, "gi");
    result = result.replace(regex, numeric);
  }

  // 2. Convert "X point Y" to "X.Y" (BEFORE converting word numbers)
  // Handle both "two point five" and "2 point 5"
  result = result.replace(/\b(\d+)\s+point\s+(\d+)\b/gi, "$1.$2");

  // Convert word + point + word: "two point five" first
  const wordPointPattern =
    /\b(one|two|three|four|five|six|seven|eight|nine)\s+point\s+(one|two|three|four|five|six|seven|eight|nine)/gi;
  result = result.replace(wordPointPattern, (match, first, second) => {
    const firstMap: Record<string, string> = {
      one: "1",
      two: "2",
      three: "3",
      four: "4",
      five: "5",
      six: "6",
      seven: "7",
      eight: "8",
      nine: "9",
    };
    return `${firstMap[first.toLowerCase()]}.${firstMap[second.toLowerCase()]}`;
  });

  // 3. Convert basic number words to digits
  const numberMap: Record<string, number> = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
    thirty: 30,
    forty: 40,
    fifty: 50,
    sixty: 60,
    seventy: 70,
    eighty: 80,
    ninety: 90,
    hundred: 100,
    thousand: 1000,
    dozen: 12,
  };

  // 4. Handle hundreds FIRST (before compound tens)
  // "two hundred fifty three" → "253"
  // "two hundred fifty" → "250"
  // "two hundred" → "200"
  const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
  const tens = ["twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

  for (let h = 1; h <= 9; h++) {
    const hundredWord = ones[h];
    if (!hundredWord) continue;

    // "two hundred fifty three" → "253"
    for (let t = 0; t < tens.length; t++) {
      const tensWord = tens[t];
      const tensVal = (t + 2) * 10; // twenty=20, thirty=30, etc.

      for (let o = 1; o <= 9; o++) {
        const onesWord = ones[o];
        const pattern = new RegExp(
          `\\b${hundredWord}\\s+hundred\\s+${tensWord}\\s+${onesWord}\\b`,
          "gi"
        );
        result = result.replace(pattern, `${h * 100 + tensVal + o}`);
      }

      // "two hundred fifty" → "250"
      const tensPattern = new RegExp(`\\b${hundredWord}\\s+hundred\\s+${tensWord}\\b`, "gi");
      result = result.replace(tensPattern, `${h * 100 + tensVal}`);
    }

    // "two hundred" → "200"
    const simplePattern = new RegExp(`\\b${hundredWord}\\s+hundred\\b`, "gi");
    result = result.replace(simplePattern, `${h * 100}`);
  }

  // 5. Handle compound numbers like "twenty three" → "23"
  // Pattern: (twenty|thirty|...) (one|two|three|...)
  for (const ten of tens) {
    for (const one of ones.slice(1)) {
      // Skip empty string
      const compound = `${ten} ${one}`;
      const value = numberMap[ten] + numberMap[one];
      const regex = new RegExp(`\\b${compound}\\b`, "gi");
      result = result.replace(regex, value.toString());
    }
  }

  // 6. Convert simple number words (single words)
  // Do this AFTER compound numbers to avoid breaking "twenty three" patterns
  for (const [word, num] of Object.entries(numberMap)) {
    if (word === "dozen") {
      // "dozen eggs" → "1 dozen eggs" (standalone dozen at start)
      const standaloneRegex = new RegExp(`^${word}\\b`, "gi");
      result = result.replace(standaloneRegex, `1 ${word}`);
      // DON'T convert "X dozen" → "X 12" (handled separately)
    } else if (word === "hundred" || word === "thousand") {
      // Skip "hundred" and "thousand" - already handled in compound sections
      continue;
    } else {
      // Convert number words, but check context
      // "two dozen" should stay as "2 dozen" (preserve dozen as unit)
      // But "one" before "dozen" should become "1"
      if (
        word === "one" ||
        word === "two" ||
        word === "three" ||
        word === "four" ||
        word === "five" ||
        word === "six" ||
        word === "seven" ||
        word === "eight" ||
        word === "nine" ||
        word === "ten"
      ) {
        // Convert number before "dozen": "one dozen" → "1 dozen"
        const beforeDozenRegex = new RegExp(`\\b${word}\\s+(?=dozen)`, "gi");
        result = result.replace(beforeDozenRegex, `${num} `);
      }

      // Convert standalone numbers (not followed by dozen or hundred)
      const regex = new RegExp(`\\b${word}\\b(?!\\s*(?:dozen|hundred|thousand))`, "gi");
      result = result.replace(regex, num.toString());
    }
  }

  // 7. Handle "a" or "an" as "1" when followed by a unit or noun
  result = result.replace(
    /\b(?:a|an)\s+(?=dozen|pounds?|lbs?|ounces?|oz|cups?|tablespoons?|tbsp|teaspoons?|tsp)/gi,
    "1 "
  );

  return result;
}

// ============================================================================
// Chinese Number Conversion
// ============================================================================

/**
 * Convert Chinese numbers and units to standardized format
 * Handles: Chinese numbers, dozen (打) conversion
 *
 * @example
 * convertChineseNumbers("一打鸡蛋") // "1打鸡蛋"
 * convertChineseNumbers("兩打雞蛋") // "2打雞蛋"
 * convertChineseNumbers("打鸡蛋") // "12鸡蛋"
 */
function convertChineseNumbers(text: string): string {
  let result = text;

  // 1. Convert standalone "打" (dozen) to "1打"
  // "打鸡蛋" → "1打鸡蛋" (standalone dozen at start)
  // Don't convert "X打" → "X12"
  const standaloneDaRegex = /^打/;
  result = result.replace(standaloneDaRegex, "1打");

  // 2. Convert Chinese numbers before "打" to Arabic numerals
  // "兩打鸡蛋" → "2打鸡蛋"
  const chineseNumBeforeDa =
    /([一二三四五六七八九十百千万壹貳贰參叁肆伍陸陆柒捌玖拾佰仟萬兩两零〇]+)\s*打/g;
  result = result.replace(chineseNumBeforeDa, (match, chineseNum) => {
    const arabicNum = chineseToArabicForNormalization(chineseNum);
    return `${arabicNum}打`;
  });

  return result;
}

/**
 * Convert Chinese numbers to Arabic numerals for normalization
 * Simplified version of chineseToArabic for normalization purposes
 */
function chineseToArabicForNormalization(chineseNum: string): string {
  const map: Record<string, number> = {
    零: 0,
    〇: 0,
    一: 1,
    壹: 1,
    二: 2,
    兩: 2,
    两: 2,
    貳: 2,
    贰: 2,
    三: 3,
    參: 3,
    叁: 3,
    四: 4,
    肆: 4,
    五: 5,
    伍: 5,
    六: 6,
    陸: 6,
    陆: 6,
    七: 7,
    柒: 7,
    八: 8,
    捌: 8,
    九: 9,
    玖: 9,
    十: 10,
    拾: 10,
    百: 100,
    佰: 100,
    千: 1000,
    仟: 1000,
    万: 10000,
    萬: 10000,
  };

  // Simple single digit
  if (chineseNum.length === 1 && map[chineseNum] !== undefined) {
    return map[chineseNum].toString();
  }

  // Handle complex numbers like 二十三, 五百, etc.
  let result = 0;
  let current = 0;
  let temp = 0;

  for (let i = 0; i < chineseNum.length; i++) {
    const char = chineseNum[i];
    const value = map[char];

    if (value === undefined) return chineseNum; // Not a valid Chinese number

    if (value >= 10) {
      // Multiplier (十, 百, 千, 万)
      if (temp === 0) temp = 1; // 十 = 1 * 10
      result += current * value;
      current = 0;
      temp = 0;
    } else {
      current = current * 10 + value;
      temp += value;
    }
  }

  result += current;
  return result.toString();
}

// ============================================================================
// Unit Abbreviation
// ============================================================================

/**
 * Convert written-out unit words to standard abbreviations
 *
 * @example
 * abbreviateUnits("3 pounds chicken", "en") // "3 lbs chicken"
 * abbreviateUnits("500 grams beef", "en") // "500 grams beef" (grams already good)
 */
function abbreviateUnits(text: string, locale: Locale): string {
  if (locale !== "en") {
    return text;
  }

  const unitMap: Record<string, string> = {
    pounds: "lbs",
    pound: "lb",
    ounces: "oz",
    ounce: "oz",
    tablespoons: "tbsp",
    tablespoon: "tbsp",
    teaspoons: "tsp",
    teaspoon: "tsp",
    kilograms: "kg",
    kilogram: "kg",
  };

  let result = text;
  for (const [long, short] of Object.entries(unitMap)) {
    // Use word boundary and preserve spacing
    const regex = new RegExp(`\\b${long}\\b`, "gi");
    result = result.replace(regex, short);
  }

  return result;
}

// ============================================================================
// Filler Word Removal
// ============================================================================

/**
 * Remove conversational filler words
 *
 * @example
 * removeFillers("I need some chicken please", "en") // "chicken"
 * removeFillers("can you add three apples", "en") // "3 apples"
 */
function removeFillers(text: string, locale: Locale): string {
  if (locale !== "en") {
    // TODO: Add Chinese filler words if needed (我要, 請加, etc.)
    return text;
  }

  let result = text;

  // Common conversational prefixes
  const prefixes = [
    "and",
    "about",
    "add some",
    "also",
    "approximately",
    "around",
    "can i have",
    "can i get",
    "can you add",
    "can you get",
    "could i have",
    "could you add",
    "could you get",
    "finally",
    "give me",
    "get me",
    "get some",
    "how about",
    "how about some",
    "i am looking for",
    "i am wanting",
    "i'd like",
    "i'll have",
    "i need",
    "i want",
    "i will have",
    "i would like",
    "i would want",
    "lastly",
    "let me have",
    "let me get",
    "let's get",
    "let us get",
    "let's have",
    "let us have",
    "some",
    "may i have",
    "might i have",
    "need to get",
    "please add",
    "please get",
    "shall we get",
    "shall we have",
    "should we get",
    "should we have",
    "want to get",
    "looking for",
    "looking to get",
    "need some",
    "please add",
    "please get",
    "roughly",
    "want some",
    "we need",
    "we want",
    "would like",
    "would want",
    "would you add",
    "would you get",
    "would you like",
  ];

  for (const prefix of prefixes) {
    const regex = new RegExp(`^${prefix}\\s+`, "gi");
    result = result.replace(regex, "");
  }

  // Repeat removal in case multiple prefixes appear in sequence but were
  // ordered such that an earlier pass skipped a later prefix.
  // Example: "can you add about five" — if "about" appears before
  // "can you add" in `prefixes`, a single pass wouldn't remove both.
  // Limit iterations to avoid accidental infinite loops.
  for (let iter = 0; iter < 5; iter++) {
    const before = result;
    for (const prefix of prefixes) {
      const regex = new RegExp(`^${prefix}\\s+`, "gi");
      result = result.replace(regex, "");
    }
    if (result === before) break;
  }

  // Common suffixes
  const suffixes = ["please", "thank you", "thanks"];
  for (const suffix of suffixes) {
    const regex = new RegExp(`\\s+${suffix}$`, "gi");
    result = result.replace(regex, "");
  }

  // Remove "of" after units (e.g., "2 cups of flour" → "2 cups flour")
  // Include abbreviated and full forms
  result = result.replace(
    /\b(lbs?|pounds?|oz|ounces?|kg|kilograms?|g|grams?|cups?|tbsp|tablespoons?|tsp|teaspoons?)\s+of\s+/gi,
    "$1 "
  );

  // Remove standalone "some" when not part of a prefix
  result = result.replace(/\bsome\s+/gi, "");

  return result.trim();
}

// ============================================================================
// Article Removal
// ============================================================================

/**
 * Remove leading articles (a, an, the)
 *
 * @example
 * removeArticles("a chicken", "en") // "chicken"
 * removeArticles("the tomatoes", "en") // "tomatoes"
 */
function removeArticles(text: string, locale: Locale): string {
  if (locale !== "en") {
    return text;
  }

  return text.replace(/^(?:a|an|the)\s+/gi, "").trim();
}

// ============================================================================
// Conjunction Splitting (Smart Multi-Item Detection)
// ============================================================================

/**
 * Detect and split multiple items in a single input
 *
 * Handles two cases:
 * 1. Explicit conjunctions: "eggs and milk" → "eggs\nmilk"
 * 2. Multiple quantities pattern: "two apples three bananas" → "2 apples\n3 bananas"
 *
 * Works for both typed and spoken input.
 *
 * @example
 * splitMultipleItems("eggs and milk and cheese", "en") // "eggs\nmilk\ncheese"
 * splitMultipleItems("two apples three bananas", "en") // "2 apples\n3 bananas"
 * splitMultipleItems("5 eggs 3 tomatoes 2 apples", "en") // "5 eggs\n3 tomatoes\n2 apples"
 */
function splitMultipleItems(text: string, _locale: Locale): string {
  let result = text;

  // 1. Split on explicit conjunctions "and"
  // But preserve "and" in measurements like "one and a half" (already converted by now)
  // Split "eggs and milk" → "eggs\nmilk"
  // Split "2 apples and 3 bananas" → "2 apples\n3 bananas"
  result = result.replace(/\s+and\s+/gi, "\n");

  // 2. Detect fast-speech patterns: multiple quantities in sequence
  // Pattern: [number/unit + words] [number + ...]
  // Example: "2 apples 3 bananas" → "2 apples\n3 bananas"
  // Example: "chicken 2 apples" → "chicken\n2 apples"

  // Strategy: Look for a number followed by words, then another number
  // Split before each new number (except the first one)

  // Match: (digits/fraction + optional unit + word(s)) + space + (digit)
  // Capture everything up to the next digit and split there
  const quantityPattern =
    /(\d+(?:\.\d+|\/\d+)?\s*(?:lbs?|oz|kg|g|grams?|cups?|tbsp|tsp|dozens?|ml|liters?|l)?\s+[a-z]+(?:\s+[a-z]+)*)\s+(?=\d)/gi;

  // Apply pattern multiple times to catch all splits
  for (let i = 0; i < 5; i++) {
    const before = result;
    result = result.replace(quantityPattern, "$1\n");
    if (before === result) break; // No more changes
  }

  return result;
}

// ============================================================================
// Main Transform Function
// ============================================================================
// Main Normalization Function
// ============================================================================

/**
 * Normalize ingredient text input (typed or spoken) into clean, structured format
 *
 * Converts natural/conversational language into standardized ingredient text
 * ready for parsing. Auto-detects English vs Chinese.
 *
 * Pipeline:
 * 1. Auto-detect locale from text content
 * 2. Remove filler words ("I need chicken" → "chicken")
 * 3. Convert written-out numbers ("two apples" → "2 apples")
 * 4. Abbreviate units ("pounds" → "lbs")
 * 5. Remove articles ("a chicken" → "chicken")
 * 6. Split multiple items ("eggs and milk" → "eggs\nmilk")
 *
 * @param text - Raw input text (typed, spoken, pasted, etc.)
 * @returns Normalized ingredient text ready for parseBulkIngredients
 *
 * @example
 * normalizeIngredientText("I need two dozen eggs and three apples please")
 * // → "2 dozen eggs\n3 apples"
 *
 * normalizeIngredientText("two apples three bananas")
 * // → "2 apples\n3 bananas"
 *
 * normalizeIngredientText("five hundred grams of chicken")
 * // → "500 grams chicken"
 */
export function normalizeIngredientText(text: string): string {
  if (!text || !text.trim()) {
    return "";
  }

  let transformed = text.toLowerCase().trim();

  // Auto-detect locale from text content
  const locale = detectLocale(text);

  // Pipeline order matters!
  // 1. Remove fillers first (cleans up conversational speech)
  transformed = removeFillers(transformed, locale);

  // 2. Convert numbers (before unit abbreviation to handle "two pounds" → "2 pounds")
  if (locale === "en") {
    transformed = convertSpokenNumbers(transformed, locale);
  } else {
    // Chinese number conversion
    transformed = convertChineseNumbers(transformed);
  }

  // 3. Abbreviate units (after number conversion)
  transformed = abbreviateUnits(transformed, locale);

  // 4. Remove articles (after other transformations)
  transformed = removeArticles(transformed, locale);

  // 5. Split multiple items (last step, works on cleaned text)
  transformed = splitMultipleItems(transformed, locale);

  return transformed.trim();
}

// ============================================================================
// Edge Case Handlers
// ============================================================================
